import { AxiosResponse } from "axios";

import { API } from "../index";

export type NotificationCategory =
  | "payment"
  | "usage"
  | "site_notice"
  | "marketing"
  | "mandatory";

export type NotificationChannel = "web_push" | "app_push" | "alimtalk";

export type Delivery = {
  id: number;
  channel: NotificationChannel;
  provider: string;
  target_label: string;
  state: string;
  error_message: string;
  attempted_at: string | null;
  accepted_at: string | null;
};

export type NotificationHistoryItem = {
  id: number;
  user_id: number | null;
  user_name: string;
  phone_num: string;
  category: NotificationCategory;
  event_type: string;
  title: string;
  body: string;
  status: string;
  created_at: string;
  read_at: string | null;
  deliveries: Delivery[];
};

export type CategorySetting = {
  category: NotificationCategory;
  is_enabled: boolean;
  web_push_enabled: boolean;
  app_push_enabled: boolean;
  alimtalk_enabled: boolean;
  channel_priority: NotificationChannel[];
  is_default?: boolean;
};

export type EventPolicy = {
  id?: number;
  event_type: string;
  category: NotificationCategory;
  is_enabled: boolean;
  web_push_enabled: boolean;
  app_push_enabled: boolean;
  alimtalk_enabled: boolean;
  alimtalk_template_id: number | null;
  alimtalk_template_name: string;
  channel_priority: NotificationChannel[];
};

export type NotificationSettingsResponse = {
  site_id: number;
  settings: CategorySetting[];
  event_policies: EventPolicy[];
};

export type NotificationSendPayload = {
  recipient_ids: number[];
  category: NotificationCategory;
  event_type?: string;
  title: string;
  body: string;
  web_url?: string;
  app_url?: string;
  data?: Record<string, unknown>;
  channels: NotificationChannel[];
  recipient_channel_overrides?: Array<{
    recipient_id: number;
    channels: NotificationChannel[];
  }>;
  alimtalk_template_id?: number | null;
};

export type RecipientChannelAvailability = {
  available: boolean;
  reason: string;
};

export type NotificationDeliveryCheckRecipient = {
  id: number;
  name: string;
  phone_num: string;
  site_registered: boolean;
  category_enabled: boolean;
  reason: string;
  channels: Record<NotificationChannel, RecipientChannelAvailability>;
};

export type NotificationDeliveryCheckResponse = {
  category: NotificationCategory;
  site_channels: Record<NotificationChannel, {
    site_enabled: boolean;
    configured: boolean;
    reason: string;
  }>;
  alimtalk_templates: Array<{ id: number; name: string; code: string }>;
  selected_alimtalk_template_id: number | null;
  recipients: NotificationDeliveryCheckRecipient[];
};

export type AlimTalkSender = {
  id: number;
  profile_name: string;
  business_name: string;
  business_number: string;
  status: string;
};

export type AlimTalkAccountResponse = {
  site_id: number;
  configured: boolean;
  provider_configured: boolean;
  delivery_ready: boolean;
  using_default: boolean;
  sender_key_hint: string;
  sender: AlimTalkSender | null;
  errors: string[];
};

export type AlimTalkAccountUpdatePayload = {
  send_profile_key?: string;
  profile_name: string;
  business_name: string;
  business_number?: string;
};

export const notificationCenterService = {
  getHistory: async (params?: {
    page?: number;
    category?: NotificationCategory | "";
    search?: string;
  }): Promise<AxiosResponse<{ count: number; results: NotificationHistoryItem[] }>> => {
    return API.get("/api/v2/notifications/history/", { params });
  },

  // 결정 262·388: 관리자 수동 알림톡 발송 잠금 상태(대표님 '시작' 전까지 locked)
  getSendLock: () => API.get<{ locked: boolean; reason: string }>("/api/v2/notifications/send-lock/"),

  send: async (payload: NotificationSendPayload): Promise<AxiosResponse<{ created: number; notifications: NotificationHistoryItem[] }>> => {
    return API.post("/api/v2/notifications/send/", payload);
  },

  checkDelivery: async (payload: {
    recipient_ids: number[];
    category: NotificationCategory;
    alimtalk_template_id?: number | null;
  }): Promise<AxiosResponse<NotificationDeliveryCheckResponse>> => {
    return API.post("/api/v2/notifications/delivery-check/", payload);
  },

  getSiteSettings: async (): Promise<AxiosResponse<NotificationSettingsResponse>> => {
    return API.get("/api/v2/notifications/site-settings/");
  },

  updateSiteSettings: async (payload: {
    settings?: CategorySetting[];
    event_policies?: EventPolicy[];
  }): Promise<AxiosResponse<NotificationSettingsResponse>> => {
    return API.patch("/api/v2/notifications/site-settings/", payload);
  },

  getWebPushConfig: async (): Promise<AxiosResponse<{ is_configured: boolean; public_key: string | null }>> => {
    return API.get("/api/v2/notifications/web-push/config/");
  },

  getAlimTalkAccount: async (): Promise<AxiosResponse<AlimTalkAccountResponse>> => {
    return API.get("/api/v2/notifications/alimtalk-account/");
  },

  updateAlimTalkAccount: async (
    payload: AlimTalkAccountUpdatePayload,
  ): Promise<AxiosResponse<AlimTalkAccountResponse>> => {
    return API.patch("/api/v2/notifications/alimtalk-account/", payload);
  },

  disconnectAlimTalkAccount: async (): Promise<AxiosResponse<AlimTalkAccountResponse>> => {
    return API.delete("/api/v2/notifications/alimtalk-account/");
  },
};
